/* Style video carousel — drag-based centre-peek rail.
   ---------------------------------------------------------------------------
   Progressive enhancement. Without JS the markup is a plain scroll-snap rail
   that works; this upgrades it in place by adding `is-ready`, after which the
   slides are absolutely positioned and driven by transform.

   The maths is the reference formula from the brief, unchanged:

     step        = railWidth * SLIDE_RATIO + GAP
     currentPos  = di - dragX / step
     dist        = min(|i - currentPos|, 1)
     scale       = 1 - dist * (1 - MIN_SCALE)
     opacity     = 1 - dist * (1 - MIN_OPACITY)
     x           = centreX + (i - di) * step + dragX

   Drag interpolates continuously — scale and opacity track the finger rather
   than snapping at a threshold. On release it settles to the nearest slide
   with a spring.

   INFINITE LOOP: the slide array is [last, ...clips, first]. Landing on a
   clone settles normally, then jumps to the real index with transitions off
   so the jump is invisible. `jump` is cleared on the next interaction.

   VIDEO: sources are held in data-src and only assigned when a slide first
   becomes active, so opening the page does not fetch every clip. Only the
   centred video plays.

   REDUCED MOTION: no scale, no opacity ramp, no spring, and video never
   autoplays — it falls back to the poster. The rail still steps.

   VERTICAL SCROLLING is never captured. See the axis lock on onDown/onMove —
   the rail fills most of a phone screen, so eating an upward swipe would
   strand the whole page.
*/

(function () {
  'use strict';

  var SPRING = 'transform 0.4s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.4s ease';
  var AXIS_SLOP = 6;   // px of movement before the gesture's direction is decided
  var DESKTOP = '(min-width: 990px)';
  var REDUCED = '(prefers-reduced-motion: reduce)';

  /* gap must track --rb-gap in rb-tokens.css (10 mobile / 16 desktop). It is a
     number here rather than a token because it feeds the drag maths — step =
     railWidth * ratio + gap — not just the layout. If the token moves, move
     these with it. */
  var PARAMS = {
    mobile:  { ratio: 0.65, gap: 10, minScale: 0.78, minOpacity: 0.85 },
    desktop: { ratio: 0.42, gap: 16, minScale: 0.80, minOpacity: 0.85 }
  };

  function reduced() { return window.matchMedia(REDUCED).matches; }

  if (!customElements.get('rb-style-carousel')) {
    customElements.define('rb-style-carousel', class extends HTMLElement {
      connectedCallback() {
        this.rail = this.querySelector('[data-rail]');
        this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-slide]'));
        this.panels = Array.prototype.slice.call(this.querySelectorAll('[data-panel]'));
        if (!this.rail || this.slides.length < 2) return;

        // display array is [last, ...clips, first]; N is the real clip count
        this.n = this.slides.length - 2;
        this.di = 1;
        this.dragX = 0;
        this.dragging = false;
        this.jump = false;
        this.railWidth = 0;

        this.classList.add('is-ready');
        this.rail.setAttribute('tabindex', '0');
        this.rail.setAttribute('role', 'group');

        this._onDown = this.onDown.bind(this);
        this._onMove = this.onMove.bind(this);
        this._onUp = this.onUp.bind(this);
        this._onKey = this.onKey.bind(this);
        this.rail.addEventListener('pointerdown', this._onDown);
        this.rail.addEventListener('pointermove', this._onMove);
        this.rail.addEventListener('pointerup', this._onUp);
        this.rail.addEventListener('pointercancel', this._onUp);
        this.rail.addEventListener('keydown', this._onKey);

        this.ro = new ResizeObserver(this.measure.bind(this));
        this.ro.observe(this.rail);

        requestAnimationFrame(function () {
          this.measure();
          this.syncVideo();
          this.syncPanel();
        }.bind(this));
      }

      disconnectedCallback() {
        if (this.ro) this.ro.disconnect();
      }

      params() {
        return window.matchMedia(DESKTOP).matches ? PARAMS.desktop : PARAMS.mobile;
      }

      measure() {
        var w = this.rail.clientWidth;
        if (w && w !== this.railWidth) {
          this.railWidth = w;
          this.render();
        }
      }

      realIndex() {
        return ((this.di - 1) % this.n + this.n) % this.n;
      }

      render() {
        var p = this.params();
        var soft = reduced();
        var slideWidth = this.railWidth * p.ratio;
        var step = slideWidth + p.gap;
        var centreX = this.railWidth / 2 - slideWidth / 2;
        var currentPos = this.di - this.dragX / (step || 1);
        var transition = (this.jump || this.dragging) ? 'none' : SPRING;

        for (var i = 0; i < this.slides.length; i++) {
          var dist = Math.min(Math.abs(i - currentPos), 1);
          var scale = soft ? 1 : 1 - dist * (1 - p.minScale);
          var opacity = soft ? (dist >= 1 ? 0.85 : 1) : 1 - dist * (1 - p.minOpacity);
          var x = centreX + (i - this.di) * step + this.dragX;
          var el = this.slides[i];
          el.style.width = Math.round(slideWidth) + 'px';
          el.style.transition = soft ? 'none' : transition;
          el.style.transform = 'translate(' + Math.round(x) + 'px, -50%) scale(' + scale.toFixed(3) + ')';
          el.style.opacity = opacity.toFixed(3);
          // only the centred slide is reachable by tab / screen reader
          el.setAttribute('aria-hidden', i === this.di ? 'false' : 'true');
        }
      }

      settle(next) {
        var clamped = Math.max(0, Math.min(this.n + 1, next));
        this.di = clamped;
        this.dragX = 0;
        this.dragging = false;
        this.jump = false;
        this.render();
        this.syncVideo();
        this.syncPanel();

        if (clamped === 0 || clamped === this.n + 1) {
          window.clearTimeout(this._wrap);
          this._wrap = window.setTimeout(function () {
            this.di = clamped === 0 ? this.n : 1;
            this.jump = true;          // transitions off for the swap
            this.render();
            // The real slide and its clone show the same clip, so the panel
            // does not change — but they are different <video> elements.
            // Without this the clone's video keeps playing off-screen while
            // the newly centred real slide sits silent and never loads.
            this.syncVideo();
          }.bind(this), 420);
        }
      }

      /* GESTURE AXIS LOCK.
         The rail must not eat vertical scrolling — on a phone the carousel is
         most of the viewport, so swallowing an upward swipe strands the page.

         Two layers do the work:
           1. touch-action: pan-y on the rail. The browser keeps vertical
              panning for itself and only forwards horizontal intent. Most
              gestures never reach this code at all.
           2. this axis lock, for pointer devices and for the ambiguous first
              few pixels of a touch.

         The axis is decided once, on the first move that clears AXIS_SLOP, and
         then held for the rest of the gesture. Re-deciding mid-swipe is what
         makes a carousel feel like it is fighting the finger.

         Pointer capture is deliberately NOT taken on pointerdown — capturing
         early suppresses the browser's own scrolling. It is taken only once
         the gesture is known to be horizontal. */
      onDown(e) {
        this.startX = e.clientX;
        this.startY = e.clientY;
        this.axis = null;          // null = undecided, 'x' = drag, 'y' = let it scroll
        this.pointerId = e.pointerId;
        this.dragging = false;
        this.dragX = 0;
        this.jump = false;
      }

      onMove(e) {
        if (this.axis === 'y') return;              // page is scrolling; stay out of it

        var dx = e.clientX - this.startX;
        var dy = e.clientY - this.startY;

        if (this.axis === null) {
          if (Math.abs(dx) < AXIS_SLOP && Math.abs(dy) < AXIS_SLOP) return;  // still ambiguous
          this.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
          if (this.axis === 'y') return;
          this.dragging = true;
          try { this.rail.setPointerCapture(this.pointerId); } catch (err) {}
        }

        e.preventDefault();
        this.dragX = dx;
        this.render();
      }

      onUp() {
        var wasDragging = this.dragging && this.axis === 'x';
        this.axis = null;
        if (!wasDragging) {
          // a vertical or tap gesture — leave the rail exactly where it was
          this.dragging = false;
          this.dragX = 0;
          return;
        }
        var p = this.params();
        var step = this.railWidth * p.ratio + p.gap;
        var moved = Math.round(-this.dragX / (step || 1));
        this.settle(this.di + moved);
      }

      onKey(e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.settle(this.di - 1); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); this.settle(this.di + 1); }
      }

      syncVideo() {
        var active = this.realIndex();
        for (var i = 0; i < this.slides.length; i++) {
          var v = this.slides[i].querySelector('video');
          if (!v) continue;
          var isActive = i === this.di;
          if (isActive && !reduced()) {
            // fetch the file only once this clip is actually shown
            var src = v.getAttribute('data-src');
            if (src && !v.src) v.src = src;
            if (v.paused) { var pr = v.play(); if (pr && pr.catch) pr.catch(function () {}); }
          } else if (!v.paused) {
            v.pause();
          }
        }
        this.setAttribute('data-active', String(active));
      }

      syncPanel() {
        var active = this.realIndex();
        for (var i = 0; i < this.panels.length; i++) {
          var on = i === active;
          this.panels[i].hidden = !on;
        }
      }
    });
  }
})();
